import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase, businesses } from '@/integrations/supabase/client';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink, ArrowLeft, Download, Briefcase, Calendar, Users, Globe, Mail, Expand, X, Puzzle, FileText, Rocket, DollarSign, PieChart, Lightbulb, Target, Award, Zap, BarChart3, TrendingUp, Shield, Image } from 'lucide-react';
import type { Business } from '@/types/business';
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { formatTextContent } from '@/lib/utils/text';
import { BusinessGalleryView } from '@/components/business/BusinessGalleryView';
import { useAuth } from '@/contexts/AuthContext';

interface PlatformFeature {
  id: string;
  title: string;
  description: string;
  icon_name: string | null;
  order_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface BusinessImage {
  id: string;
  url: string;
  caption: string;
}

interface CompanyProfile {
  id: string;
  company_name: string;
  tagline: string | null;
  description: string;
  logo_url: string | null;
  pitch_deck_url: string | null;
  development_stage: string;
  industry_type: string;
  website: string | null;
  contact_email: string | null;
  problem_statement: string | null;
  solution_description: string | null;
  market_size: string | null;
  funding_goal: number | null;
  investment_highlights: string[] | null;
  competitive_edge: string[] | null;
  target_segment: string | null;
  growth_drivers: string | null;
  team_members: any | null;
  business_model: any | null;
  traction: any | null;
  product_offerings: any | null;
  financial_projections: any | null;
  entity_type: string;
  status: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  features: PlatformFeature[];
  gallery_images: BusinessImage[];
}

const formatDisplayText = (text: string): string => {
  // Replace "\n-" with newline and bullet point
  let formatted = text.replace(/\\n-\s*/g, '\n• ');
  // Replace remaining "\n" with newlines
  formatted = formatted.replace(/\\n/g, '\n');
  // If the text starts with a dash, replace it with a bullet point
  formatted = formatted.replace(/^-\s*/, '• ');
  return formatted;
};

interface CompanyPageProps {
  isEditing?: boolean;
}

const fetchRelatedCompanies = async (industryType: string) => {
  const { data } = await supabase
    .from('businesses')
    .select('*')
    .eq('industry_type', industryType)
    .eq('status', 'active')
    .limit(3);
  return data;
};

const fetchCompanyDetails = async (id: string): Promise<CompanyProfile> => {
  if (!id) throw new Error('Company ID is required');

  // First check if user has permission to view this company
  const { data: { user } } = await supabase.auth.getUser();
  
  // Fix profile query by adding user_id filter
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('user_type')
    .eq('user_id', user?.id)
    .single();

  const isAdmin = profile?.user_type === 'admin';
  
  // Fetch basic company details
  const { data: companyData, error: companyError } = await supabase
    .from('businesses')
    .select('*')
    .eq('id', id)
    .single();

  if (companyError) {
    throw new Error('Failed to load company details');
  }

  if (!companyData) {
    throw new Error('Company not found');
  }

  // Check if user has permission to view this company
  const hasPermission = isAdmin || 
                      companyData.status === 'active' || 
                      (user && companyData.user_id === user.id);

  if (!hasPermission) {
    throw new Error('You do not have permission to view this company');
  }

  // Sanitize sensitive data for non-owners/non-admins
  if (!isAdmin && (!user || companyData.user_id !== user.id)) {
    delete companyData.funding_goal;
    delete companyData.business_model;
    delete companyData.financial_projections;
    delete companyData.team_members;
  }

  // Fetch features
  const { data: featureLinks } = await supabase
    .from('business_features')
    .select('feature_id')
    .eq('business_id', id);

  let features = [];
  if (featureLinks?.length) {
    const { data: featureData } = await supabase
      .from('platform_features')
      .select('*')
      .in('id', featureLinks.map(link => link.feature_id))
      .eq('is_active', true)
      .order('order_index', { ascending: true });

    if (featureData) {
      features = featureData.map(feature => ({
        id: feature.id,
        title: feature.name,
        description: feature.description,
        icon_name: feature.icon,
        order_index: feature.order_index,
        is_active: feature.is_active,
        created_at: feature.created_at,
        updated_at: feature.updated_at
      }));
    }
  }

  return {
    ...companyData,
    features,
    gallery_images: [] // Initialize with empty array since gallery table doesn't exist yet
  };
};

const CompanyPage = ({ isEditing = false }: CompanyPageProps) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isImageOpen, setIsImageOpen] = useState(false);

  // Validate UUID format
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const isValidId = id && uuidRegex.test(id);

  const { 
    data: company,
    isLoading,
    isError,
    error
  } = useQuery<CompanyProfile, Error>({
    queryKey: ['company', id],
    queryFn: () => fetchCompanyDetails(id!),
    enabled: !!id && isValidId,
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    retry: 1,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true
  });

  // Prefetch related companies
  React.useEffect(() => {
    if (company?.industry_type) {
      queryClient.prefetchQuery({
        queryKey: ['related-companies', company.industry_type],
        queryFn: () => fetchRelatedCompanies(company.industry_type),
        staleTime: 1000 * 60 * 5
      });
    }
  }, [company?.industry_type, queryClient]);

  const handleBack = () => {
    navigate(-1);
  };

  // If in edit mode but no id, redirect back
  if (isEditing && !id) {
    toast.error('No company selected for editing');
    navigate(-1);
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-16">
          <div className="space-y-8 animate-pulse">
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-6 w-1/2" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Skeleton className="h-64 rounded-lg" />
              <div className="md:col-span-2 space-y-4">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-8 w-2/3" />
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show error state
  if (isError || !isValidId) {
    const errorMessage = !isValidId 
      ? 'Invalid company identifier' 
      : error instanceof Error 
        ? error.message 
        : 'An unexpected error occurred';

    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-700 mb-8">{errorMessage}</p>
          <Button onClick={handleBack}>Go Back</Button>
        </div>
        <Footer />
      </div>
    );
  }

  // Show not found state
  if (!company) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold mb-4">Company Not Found</h1>
          <p className="text-gray-700 mb-8">The company you're looking for doesn't exist or has been removed.</p>
          <Button onClick={handleBack}>Go Back</Button>
        </div>
        <Footer />
      </div>
    );
  }

  // Map icon names to actual Lucide icons
  const iconMap: Record<string, React.ReactNode> = {
    BarChart3: <BarChart3 className="h-6 w-6 text-blue-600" />,
    Users: <Users className="h-6 w-6 text-blue-600" />,
    Shield: <Shield className="h-6 w-6 text-blue-600" />,
    Briefcase: <Briefcase className="h-6 w-6 text-blue-600" />,
    PieChart: <PieChart className="h-6 w-6 text-blue-600" />,
    TrendingUp: <TrendingUp className="h-6 w-6 text-blue-600" />,
    FileText: <FileText className="h-6 w-6 text-blue-600" />,
    DollarSign: <DollarSign className="h-6 w-6 text-blue-600" />,
    Rocket: <Rocket className="h-6 w-6 text-blue-600" />,
    Target: <Target className="h-6 w-6 text-blue-600" />,
    Lightbulb: <Lightbulb className="h-6 w-6 text-blue-600" />,
    Award: <Award className="h-6 w-6 text-blue-600" />,
    Zap: <Zap className="h-6 w-6 text-blue-600" />,
    Globe: <Globe className="h-6 w-6 text-blue-600" />,
    Puzzle: <Puzzle className="h-6 w-6 text-blue-600" />
  };

  const { mutate: updateCompany } = useMutation({
    mutationFn: async (updatedData: Partial<CompanyProfile>) => {
      const { data, error } = await supabase
        .from('businesses')
        .update(updatedData)
        .eq('id', id!)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', id] });
      toast.success('Company details updated successfully');
      navigate(`/company/${id}`);
    },
    onError: (error) => {
      console.error('Error updating company:', error);
      toast.error('Failed to update company details');
    }
  });

  const handleSave = async () => {
    if (!company) return;

    try {
      await updateCompany({
        company_name: company.company_name,
        tagline: company.tagline,
        description: company.description,
        development_stage: company.development_stage,
        industry_type: company.industry_type,
        website: company.website,
        contact_email: company.contact_email,
        problem_statement: company.problem_statement,
        solution_description: company.solution_description,
        market_size: company.market_size,
        funding_goal: company.funding_goal,
        investment_highlights: company.investment_highlights,
        competitive_edge: company.competitive_edge,
        target_segment: company.target_segment,
        growth_drivers: company.growth_drivers,
        team_members: company.team_members,
        business_model: company.business_model,
        traction: company.traction,
        product_offerings: company.product_offerings,
        financial_projections: company.financial_projections
      });
    } catch (error) {
      console.error('Error in handleSave:', error);
    }
  };

  const handleCancel = () => {
    navigate('/entrepreneur');
  };

  const handleInputChange = (field: keyof CompanyProfile, value: any) => {
    if (company) {
      // Validate numeric fields
      if (field === 'funding_goal') {
        const numValue = Number(value);
        if (isNaN(numValue) || numValue < 0) {
          toast.error('Please enter a valid funding goal');
          return;
        }
      }

      // Sanitize input
      let sanitizedValue = value;
      if (typeof value === 'string') {
        // Remove any potentially harmful HTML/script tags
        sanitizedValue = value.replace(/<[^>]*>/g, '');
      }

      setCompany({ ...company, [field]: sanitizedValue });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />
      <div className="container mx-auto px-4 py-8 md:py-16">
        <div className="flex justify-between items-center mb-8">
          <Button variant="outline" onClick={isEditing ? handleCancel : handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" /> {isEditing ? 'Cancel' : 'Back'}
          </Button>
          {isEditing && (
            <div className="space-x-4">
              <Button variant="outline" onClick={handleCancel}>Cancel</Button>
              <Button onClick={handleSave}>Save Changes</Button>
            </div>
          )}
        </div>
        
        <div className="space-y-8">
          {/* Hero Section */}
          <div className="bg-white rounded-xl shadow-sm p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                {isEditing ? (
                  <div className="space-y-4">
                    <input
                      type="text"
                      value={company.company_name}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      className="w-full text-4xl font-bold p-2 border rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      value={company.tagline || ''}
                      onChange={(e) => handleInputChange('tagline', e.target.value)}
                      placeholder="Add a tagline"
                      className="w-full text-xl text-gray-600 p-2 border rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ) : (
                  <>
                    <h1 className="text-4xl font-bold mb-4">{company.company_name}</h1>
                    <p className="text-xl text-gray-600 mb-6">{company.tagline}</p>
                  </>
                )}
                <div className="flex flex-wrap gap-4">
                  {isEditing ? (
                    <div className="w-full space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                        <input
                          type="url"
                          value={company.website || ''}
                          onChange={(e) => handleInputChange('website', e.target.value)}
                          placeholder="https://example.com"
                          className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                        <input
                          type="email"
                          value={company.contact_email || ''}
                          onChange={(e) => handleInputChange('contact_email', e.target.value)}
                          placeholder="contact@example.com"
                          className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      {company.website && (
                        <div className="bg-white p-6 rounded-xl shadow-sm">
                          <div className="flex items-center gap-3 mb-4">
                            <Globe className="h-6 w-6 text-blue-600" />
                            <h3 className="text-xl font-semibold">Website</h3>
                          </div>
                          <div className="flex items-center gap-4">
                            <a 
                              href={company.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
                            >
                              <Globe className="h-4 w-4" />
                              Visit Website
                            </a>
                          </div>
                        </div>
                      )}
                      {company.contact_email && (
                        <a href={company.website} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" className="gap-2">
                            <Globe className="h-4 w-4" />
                            Visit Website
                          </Button>
                        </a>
                      )}
                      {company.contact_email && (
                        <a href={`mailto:${company.contact_email}`}>
                          <Button variant="outline" className="gap-2">
                            <Mail className="h-4 w-4" />
                            Contact
                          </Button>
                        </a>
                      )}
                    </>
                  )}
                  {company.pitch_deck_url && (
                    <a href={company.pitch_deck_url} target="_blank" rel="noopener noreferrer">
                      <Button className="gap-2">
                        <FileText className="h-4 w-4" />
                        View Pitch Deck
                      </Button>
                    </a>
                  )}
                </div>
              </div>
              <div className="flex justify-center">
                {company.logo_url ? (
                  <img 
                    src={company.logo_url} 
                    alt={`${company.company_name} logo`} 
                    className="w-48 h-48 object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.parentElement!.innerHTML = `
                        <div class="flex items-center justify-center w-48 h-48 bg-gray-100 rounded-lg">
                          <span class="text-6xl font-bold text-h44s-navy">
                            ${(company.company_name || "").charAt(0)}
                          </span>
                        </div>
                      `;
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center w-48 h-48 bg-gray-100 rounded-lg">
                    <span className="text-6xl font-bold text-h44s-navy">
                      {(company.company_name || "").charAt(0)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <Rocket className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-semibold">Development Stage</h3>
              </div>
              {isEditing ? (
                <select
                  value={company.development_stage}
                  onChange={(e) => handleInputChange('development_stage', e.target.value)}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                >
                  <option value="idea">Idea Stage</option>
                  <option value="mvp">MVP</option>
                  <option value="development">In Development</option>
                  <option value="beta">Beta</option>
                  <option value="launched">Launched</option>
                  <option value="growth">Growth</option>
                  <option value="scale">Scale</option>
                </select>
              ) : (
                <p className="text-gray-700">{company.development_stage || 'Not specified'}</p>
              )}
            </div>
            
            {(isEditing || company.funding_goal > 0) && (
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                  <h3 className="text-lg font-semibold">Funding Goal</h3>
                </div>
                {isEditing ? (
                  <input
                    type="number"
                    value={company.funding_goal || ''}
                    onChange={(e) => handleInputChange('funding_goal', e.target.value ? Number(e.target.value) : null)}
                    placeholder="Enter funding goal"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-700">${company.funding_goal.toLocaleString()}</p>
                )}
              </div>
            )}

            {company.market_size && (
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <PieChart className="h-6 w-6 text-blue-600" />
                  <h3 className="text-lg font-semibold">Market Value</h3>
                </div>
                {isEditing ? (
                  <input
                    type="text"
                    value={company.market_size || ''}
                    onChange={(e) => handleInputChange('market_size', e.target.value)}
                    placeholder="e.g. $1B by 2025"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-700">{company.market_size || 'Not specified'}</p>
                )}
              </div>
            )}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              {(isEditing || company.problem_statement) && (
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <Lightbulb className="h-6 w-6 text-blue-600" />
                    <h3 className="text-lg font-semibold">Problem</h3>
                  </div>
                  {isEditing ? (
                    <textarea
                      value={company.problem_statement || ''}
                      onChange={(e) => handleInputChange('problem_statement', e.target.value)}
                      placeholder="Describe the problem your company solves"
                      rows={4}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="text-gray-700">{company.problem_statement}</p>
                  )}
                </div>
              )}

              {(isEditing || company.target_segment) && (
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <Target className="h-6 w-6 text-blue-600" />
                    <h3 className="text-lg font-semibold">Target Market</h3>
                  </div>
                  {isEditing ? (
                    <textarea
                      value={company.target_segment || ''}
                      onChange={(e) => handleInputChange('target_segment', e.target.value)}
                      placeholder="Describe your target market (e.g. Small and Medium Businesses in South Africa)"
                      rows={4}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <div className="text-gray-700 whitespace-pre-wrap">
                      {company.target_segment || 'No target market specified'}
                    </div>
                  )}
                </div>
              )}

              {(isEditing || (company.investment_highlights && company.investment_highlights.length > 0)) && (
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <Award className="h-6 w-6 text-blue-600" />
                    <h3 className="text-lg font-semibold">Investment Highlights</h3>
                  </div>
                  {isEditing ? (
                    <textarea
                      value={(company.investment_highlights || []).join('\n')}
                      onChange={(e) => handleInputChange('investment_highlights', e.target.value.split('\n').filter(Boolean))}
                      placeholder="Enter investment highlights (one per line)"
                      rows={6}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      {(company.investment_highlights || []).map((highlight, index) => (
                        <li key={index}>{highlight}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Center Column */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="h-6 w-6 text-blue-600" />
                  <h2 className="text-2xl font-bold">About {company.company_name}</h2>
                </div>
                <div className="prose max-w-none text-gray-700">
                  <p>{company.description || 'No description available.'}</p>
                </div>
              </div>

              {(isEditing || company.solution_description) && (
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-6 w-6 text-blue-600" />
                    <h3 className="text-xl font-semibold">Solution</h3>
                  </div>
                  {isEditing ? (
                    <textarea
                      value={company.solution_description || ''}
                      onChange={(e) => handleInputChange('solution_description', e.target.value)}
                      placeholder="Describe your company's solution"
                      rows={4}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="text-gray-700">{company.solution_description}</p>
                  )}
                </div>
              )}

              {(isEditing || (company.competitive_edge && company.competitive_edge.length > 0)) && (
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="h-6 w-6 text-blue-600" />
                    <h3 className="text-xl font-semibold">Competitive Edge</h3>
                  </div>
                  {isEditing ? (
                    <textarea
                      value={(company.competitive_edge || []).join('\n')}
                      onChange={(e) => handleInputChange('competitive_edge', e.target.value.split('\n').filter(Boolean))}
                      placeholder="Enter competitive advantages (one per line)"
                      rows={6}
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      {(company.competitive_edge || []).map((edge, index) => (
                        <li key={index}>{edge}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {company.business_model && (
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <BarChart3 className="h-6 w-6 text-blue-600" />
                    <h3 className="text-xl font-semibold">Business Model</h3>
                  </div>
                  <div className="space-y-4">
                    {typeof company.business_model === 'object' && company.business_model !== null ? (
                      Object.entries(company.business_model).map(([key, value]) => (
                        <div key={key} className="border-b border-gray-100 pb-4 last:border-0">
                          <h4 className="font-medium text-gray-900 mb-2 capitalize">{key.replace(/_/g, ' ')}</h4>
                          <pre className="text-gray-700 whitespace-pre-wrap font-sans">
                            {Array.isArray(value) 
                              ? value.map(item => `• ${item}`).join('\n')
                              : typeof value === 'object' 
                                ? JSON.stringify(value, null, 2)
                                : formatDisplayText(String(value))
                            }
                          </pre>
                        </div>
                      ))
                    ) : (
                      <pre className="text-gray-700 whitespace-pre-wrap font-sans">
                        {formatDisplayText(String(company.business_model))}
                      </pre>
                    )}
                  </div>
                </div>
              )}

              {company.traction && (
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                    <h3 className="text-xl font-semibold">Traction</h3>
                  </div>
                  <div className="space-y-4">
                    {typeof company.traction === 'object' && company.traction !== null ? (
                      Object.entries(company.traction).map(([key, value]) => (
                        <div key={key} className="border-b border-gray-100 pb-4 last:border-0">
                          <h4 className="font-medium text-gray-900 mb-2 capitalize">{key.replace(/_/g, ' ')}</h4>
                          <pre className="text-gray-700 whitespace-pre-wrap font-sans">
                            {Array.isArray(value) 
                              ? value.map(item => `• ${item}`).join('\n')
                              : typeof value === 'object' 
                                ? JSON.stringify(value, null, 2)
                                : formatDisplayText(String(value))
                            }
                          </pre>
                        </div>
                      ))
                    ) : (
                      <pre className="text-gray-700 whitespace-pre-wrap font-sans">
                        {formatDisplayText(String(company.traction))}
                      </pre>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Platform Features Section */}
          {company.features && company.features.length > 0 && (
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <Puzzle className="h-6 w-6 text-blue-600" />
                <h3 className="text-xl font-semibold">Platform Features</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {company.features.map((feature) => (
                  <div key={feature.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      {feature.icon_name && iconMap[feature.icon_name as keyof typeof iconMap] || 
                       <Puzzle className="h-6 w-6 text-blue-600" />}
                      <h4 className="font-semibold">{feature.title}</h4>
                    </div>
                    <p className="text-gray-700 text-sm">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gallery Section */}
          {company.gallery_images && company.gallery_images.length > 0 && (
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <Image className="h-6 w-6 text-blue-600" />
                <h3 className="text-xl font-semibold">Gallery</h3>
              </div>
              <BusinessGalleryView images={company.gallery_images} />
            </div>
          )}

          {/* Chat with Amrish Section */}
          <div className="bg-white p-8 rounded-xl shadow-sm text-center">
            <h3 className="text-2xl font-bold mb-4">Want to learn more?</h3>
            <p className="text-gray-600 mb-6">Get in touch with Amrish to discuss your business needs and opportunities.</p>
            <Button 
              onClick={() => navigate('/chat-with-amrish')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full text-lg font-semibold"
            >
              <Mail className="h-5 w-5 mr-2" />
              Chat with Amrish
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CompanyPage;
